import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { X, Upload, FileText, Calendar, DollarSign, Users as UsersIcon, AlertCircle, UserPlus, Trash2, Check, Copy, Search, ChevronDown } from 'lucide-react';
import axios from 'axios';
import AmendmentHistory from './AmendmentHistory';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const ContractForm = ({ contract, onClose, onSave }) => {
  const { user } = useContext(UserContext);
  const [users, setUsers] = useState([]);
  const [showAmendments, setShowAmendments] = useState(false);
  const [formData, setFormData] = useState({
    user: contract?.user || '',
    company: contract?.company || user?.company || '',
    contract_number: contract?.contract_number || '',
    creation_date: contract?.creation_date || new Date().toISOString().split('T')[0],
    start_date: contract?.start_date || '',
    end_date: contract?.end_date || '',
    status: contract?.status || 'active',
    current_rate: contract?.current_rate || '',
    currency: contract?.currency || 'EUR',
    rate_type: contract?.rate_type || 'hourly',
    notes: contract?.notes || '',
    pdf_document: null
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // User search state
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);

  // New state for added people list
  const [addedPeople, setAddedPeople] = useState([]);
  const [selectedPersonIndex, setSelectedPersonIndex] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter(u => {
    const searchLower = userSearchTerm.toLowerCase();
    const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
    const email = (u.email || '').toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  // Get selected user display name
  const getSelectedUserName = () => {
    if (!formData.user) return '';
    const selectedUser = users.find(u => u.id === parseInt(formData.user));
    return selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : '';
  };

  // Handle user selection
  const handleUserSelect = (userId) => {
    setFormData(prev => ({ ...prev, user: userId }));
    setShowUserDropdown(false);
    setUserSearchTerm('');
    if (errors.user) {
      setErrors(prev => ({ ...prev, user: null }));
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));

      // Clear end_date if marking as indefinite
      if (name === 'end_date' && !value) {
        setFormData(prev => ({ ...prev, is_indefinite: true }));
      }
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.user) newErrors.user = 'User is required';
    if (!formData.contract_number) newErrors.contract_number = 'Contract number is required';
    if (!formData.start_date) newErrors.start_date = 'Start date is required';
    if (!formData.current_rate || formData.current_rate <= 0) {
      newErrors.current_rate = 'Valid rate is required';
    }

    // Validate end_date if provided
    if (formData.end_date && formData.start_date) {
      if (new Date(formData.end_date) <= new Date(formData.start_date)) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add person to the list (without creating contract yet)
  const handleAddPerson = () => {
    if (!validate()) return;

    const selectedUser = users.find(u => u.id === parseInt(formData.user));
    const newPerson = {
      ...formData,
      user_name: selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : '',
      user_email: selectedUser?.email || '',
      id: Date.now() // temporary ID for list management
    };

    if (selectedPersonIndex !== null) {
      // Update existing person in list
      setAddedPeople(prev => prev.map((p, idx) =>
        idx === selectedPersonIndex ? newPerson : p
      ));
      setSelectedPersonIndex(null);
    } else {
      // Add new person to list
      setAddedPeople(prev => [...prev, newPerson]);
    }

    // Clear only user field, keep other data for quick next entry
    setFormData(prev => ({
      ...prev,
      user: '',
      contract_number: generateNextContractNumber(prev.contract_number)
    }));
    setErrors({});
  };

  // Generate next contract number
  const generateNextContractNumber = (currentNumber) => {
    if (!currentNumber) return '';
    const match = currentNumber.match(/^(.+?)(\d+)$/);
    if (match) {
      const prefix = match[1];
      const number = parseInt(match[2]) + 1;
      return `${prefix}${number.toString().padStart(match[2].length, '0')}`;
    }
    return currentNumber;
  };

  // Select person from list to edit
  const handleSelectPerson = (index) => {
    const person = addedPeople[index];
    setFormData({
      user: person.user,
      company: person.company,
      contract_number: person.contract_number,
      creation_date: person.creation_date,
      start_date: person.start_date,
      end_date: person.end_date,
      status: person.status,
      current_rate: person.current_rate,
      currency: person.currency,
      rate_type: person.rate_type,
      notes: person.notes,
      pdf_document: null
    });
    setSelectedPersonIndex(index);
    setErrors({});
  };

  // Remove person from list
  const handleRemovePerson = (index) => {
    setAddedPeople(prev => prev.filter((_, idx) => idx !== index));
    if (selectedPersonIndex === index) {
      setSelectedPersonIndex(null);
    } else if (selectedPersonIndex > index) {
      setSelectedPersonIndex(prev => prev - 1);
    }
  };

  // Create all contracts
  const handleCreateAllContracts = async () => {
    if (addedPeople.length === 0) {
      alert('Add at least one person to the list first');
      return;
    }

    setLoading(true);
    const results = { success: 0, failed: 0 };

    for (const person of addedPeople) {
      try {
        const submitData = new FormData();
        Object.keys(person).forEach(key => {
          if (['id', 'user_name', 'user_email'].includes(key)) return;
          if (key === 'pdf_document') {
            if (person[key] instanceof File) {
              submitData.append(key, person[key]);
            }
          } else if (person[key] !== null && person[key] !== '') {
            submitData.append(key, person[key]);
          }
        });

        await axios.post(
          `${API_BASE_URL}/contracts/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        results.success++;
      } catch (error) {
        console.error('Error creating contract:', error);
        results.failed++;
      }
    }

    setLoading(false);

    if (results.failed === 0) {
      onSave();
    } else {
      alert(`Created ${results.success} contracts. ${results.failed} failed.`);
      if (results.success > 0) {
        onSave();
      }
    }
  };

  // Original submit for editing mode
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const submitData = new FormData();

      // Add all text fields
      Object.keys(formData).forEach(key => {
        if (key === 'pdf_document') {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          }
        } else if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      if (contract) {
        // Update existing contract
        await axios.patch(
          `${API_BASE_URL}/contracts/${contract.id}/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        // Create new contract
        await axios.post(
          `${API_BASE_URL}/contracts/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      onSave();
    } catch (error) {
      console.error('Error saving contract:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        alert('Error saving contract. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Copy data from selected person (for duplicating settings)
  const handleCopyFromPerson = (index) => {
    const person = addedPeople[index];
    setFormData(prev => ({
      ...prev,
      company: person.company,
      start_date: person.start_date,
      end_date: person.end_date,
      status: person.status,
      current_rate: person.current_rate,
      currency: person.currency,
      rate_type: person.rate_type,
      notes: person.notes
    }));
  };

  // Check if we're in edit mode (has existing contract)
  const isEditMode = contract?.id;

  // Compact form fields for Quick Add mode (no scrolling)
  const renderCompactFormFields = () => (
    <>
      {/* Row 1: User Selection with Search */}
      <div ref={userDropdownRef} className="relative">
        <label className="block text-xs font-medium text-gray-700 mb-0.5">User *</label>
        <div
          onClick={() => setShowUserDropdown(!showUserDropdown)}
          className={`w-full px-2 py-1.5 text-sm border rounded cursor-pointer flex items-center justify-between ${
            errors.user ? 'border-red-500' : 'border-gray-300'
          } hover:border-gray-400 bg-white`}
        >
          <span className={formData.user ? 'text-gray-900' : 'text-gray-400'}>
            {getSelectedUserName() || 'Select user...'}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
        </div>

        {/* Dropdown */}
        {showUserDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* User List */}
            <div className="max-h-48 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">No users found</div>
              ) : (
                filteredUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => handleUserSelect(u.id)}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${
                      formData.user === u.id ? 'bg-blue-100' : ''
                    }`}
                  >
                    <div className="font-medium">{u.first_name} {u.last_name}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {errors.user && <p className="text-xs text-red-600">{errors.user}</p>}
      </div>

      {/* Row 2: Contract Number + Creation Date */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">Contract Number *</label>
          <input
            type="text"
            name="contract_number"
            value={formData.contract_number}
            onChange={handleChange}
            placeholder="EMP-2024-001"
            className={`w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              errors.contract_number ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.contract_number && <p className="text-xs text-red-600">{errors.contract_number}</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">Creation Date</label>
          <input
            type="date"
            name="creation_date"
            value={formData.creation_date}
            onChange={handleChange}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Row 3: Start/End Dates + Status */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">Start Date *</label>
          <input
            type="date"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
            className={`w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              errors.start_date ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.start_date && <p className="text-xs text-red-600">{errors.start_date}</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">End Date <span className="text-gray-400">(opt.)</span></label>
          <input
            type="date"
            name="end_date"
            value={formData.end_date}
            onChange={handleChange}
            className={`w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              errors.end_date ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.end_date && <p className="text-xs text-red-600">{errors.end_date}</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
          </select>
        </div>
      </div>

      {/* Row 4: Rate, Currency, Rate Type */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">Rate *</label>
          <input
            type="number"
            name="current_rate"
            value={formData.current_rate}
            onChange={handleChange}
            step="0.01"
            min="0"
            placeholder="0.00"
            className={`w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              errors.current_rate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.current_rate && <p className="text-xs text-red-600">{errors.current_rate}</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">Currency</label>
          <select
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="EUR">EUR</option>
            <option value="PLN">PLN</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">Rate Type</label>
          <select
            name="rate_type"
            value={formData.rate_type}
            onChange={handleChange}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      {/* Row 5: PDF + Notes inline */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">Contract PDF</label>
          <label className="flex items-center justify-center px-2 py-1.5 border border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-500 text-sm text-gray-600">
            <Upload className="w-3 h-3 mr-1 text-gray-400" />
            {formData.pdf_document?.name || 'Choose file'}
            <input
              type="file"
              name="pdf_document"
              onChange={handleChange}
              accept=".pdf"
              className="hidden"
            />
          </label>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">Notes</label>
          <input
            type="text"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Optional notes..."
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
    </>
  );

  // Helper function to render form fields (used in edit mode)
  const renderFormFields = () => (
    <>
      {/* User Selection with Search */}
      <div ref={!contract ? userDropdownRef : null} className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">User *</label>
        <div
          onClick={() => !contract && setShowUserDropdown(!showUserDropdown)}
          className={`w-full pl-10 pr-4 py-2 border rounded-md cursor-pointer flex items-center justify-between ${
            errors.user ? 'border-red-500' : 'border-gray-300'
          } ${contract ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'}`}
        >
          <div className="flex items-center">
            <UsersIcon className="absolute left-3 text-gray-400 w-4 h-4" />
            <span className={formData.user ? 'text-gray-900' : 'text-gray-400'}>
              {getSelectedUserName() || 'Select user...'}
            </span>
          </div>
          {!contract && <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />}
        </div>

        {/* Dropdown */}
        {showUserDropdown && !contract && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* User List */}
            <div className="max-h-56 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">No users found</div>
              ) : (
                filteredUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => handleUserSelect(u.id)}
                    className={`px-4 py-2 cursor-pointer hover:bg-blue-50 ${
                      formData.user === u.id ? 'bg-blue-100' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{u.first_name} {u.last_name}</div>
                    <div className="text-sm text-gray-500">{u.email}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {errors.user && (
          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.user}
          </p>
        )}
      </div>

      {/* Contract Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contract Number *</label>
        <div className="relative">
          <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            name="contract_number"
            value={formData.contract_number}
            onChange={handleChange}
            placeholder="e.g., EMP-2024-001"
            className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.contract_number ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        </div>
        {errors.contract_number && (
          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.contract_number}
          </p>
        )}
      </div>

      {/* Creation Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Creation Date</label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="date"
            name="creation_date"
            value={formData.creation_date}
            onChange={handleChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.start_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.start_date && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.start_date}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date <span className="text-xs text-gray-500">(leave blank for indefinite)</span>
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.end_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.end_date && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.end_date}
            </p>
          )}
        </div>
      </div>

      {/* Rate Information */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rate *</label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="number"
              name="current_rate"
              value={formData.current_rate}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.current_rate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {errors.current_rate && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.current_rate}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
          <select
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="EUR">EUR</option>
            <option value="PLN">PLN</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rate Type</label>
          <select
            name="rate_type"
            value={formData.rate_type}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>

      {/* PDF Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contract PDF</label>
        <div className="flex items-center gap-3">
          <label className="flex-1 flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-500">
            <Upload className="w-4 h-4 mr-2 text-gray-400" />
            <span className="text-sm text-gray-600">
              {formData.pdf_document?.name || 'Choose file or drag here'}
            </span>
            <input
              type="file"
              name="pdf_document"
              onChange={handleChange}
              accept=".pdf"
              className="hidden"
            />
          </label>
          {contract?.pdf_document && !formData.pdf_document && (
            <a
              href={contract.pdf_document}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              View current PDF
            </a>
          )}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={2}
          placeholder="Admin notes about this contract..."
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </>
  );

  if (showAmendments && contract) {
    return (
      <AmendmentHistory
        contract={contract}
        onClose={() => setShowAmendments(false)}
        onBack={() => setShowAmendments(false)}
      />
    );
  }

  // Edit mode - original single column layout
  if (contract) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">Edit Contract</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {renderFormFields()}

            {/* Actions */}
            <div className="flex justify-between items-center pt-4 border-t">
              <div>
                <button
                  type="button"
                  onClick={() => setShowAmendments(true)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Amendment History ({contract.amendments?.length || 0})
                </button>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Update Contract'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // New mode - two column layout for bulk adding
  return (
    <div className="p-4 h-full">
      <div className="flex gap-4 h-full">
        {/* Left Column - Form */}
        <div className="flex-1 bg-white rounded-lg shadow flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center px-4 py-2 border-b">
            <h2 className="text-lg font-bold text-gray-900">Quick Add Contracts</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form - compact layout */}
          <div className="p-3 space-y-2 flex-1">
            {renderCompactFormFields()}

            {/* Add to List Button */}
            <div className="flex gap-2 pt-2 border-t mt-2">
              <button
                type="button"
                onClick={handleAddPerson}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
              >
                <UserPlus className="w-4 h-4" />
                {selectedPersonIndex !== null ? 'Update in List' : 'Add to List'}
              </button>
              {selectedPersonIndex !== null && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPersonIndex(null);
                    setFormData(prev => ({
                      ...prev,
                      user: '',
                      contract_number: generateNextContractNumber(addedPeople[addedPeople.length - 1]?.contract_number || prev.contract_number)
                    }));
                  }}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Added People List */}
        <div className="w-80 bg-white rounded-lg shadow flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center px-3 py-2 border-b">
            <h3 className="text-sm font-semibold text-gray-900">
              People to Add ({addedPeople.length})
            </h3>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-2">
            {addedPeople.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <UsersIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No people added yet</p>
                <p className="text-xs">Fill the form and click "Add to List"</p>
              </div>
            ) : (
              <div className="space-y-1">
                {addedPeople.map((person, index) => (
                  <div
                    key={person.id}
                    className={`p-2 rounded border cursor-pointer transition-colors ${
                      selectedPersonIndex === index
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleSelectPerson(index)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {person.user_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {person.contract_number} • {person.current_rate} {person.currency}
                        </p>
                      </div>
                      <div className="flex gap-0.5 ml-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyFromPerson(index);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Copy settings to form"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePerson(index);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Remove from list"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create All Button */}
          <div className="p-3 border-t">
            <button
              onClick={handleCreateAllContracts}
              disabled={loading || addedPeople.length === 0}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? (
                'Creating...'
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Create {addedPeople.length} Contract{addedPeople.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full mt-1 px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractForm;
